/**
 * Provider Detail Modal
 * Shows comprehensive provider information with performance metrics
 */

import { X, Building, MapPin, Phone, Mail, Globe, Star, CheckCircle, Clock, Target, TrendingUp, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface ProviderMetrics {
  id: string;
  name: string;
  organizationType: string;
  zipCode: string;
  city?: string;
  state?: string;
  services: string[];
  referralsAccepted: number;
  referralsMatched: number;
  avgMatchToCloseTime: number;
  dataQualityScore: number;
  googleRating: number | null;
  googleReviewCount: number;
  phoneNumber?: string;
  email?: string;
  website?: string;
  streetAddress?: string;
}

interface ProviderDetailModalProps {
  provider: ProviderMetrics;
  onClose: () => void;
}

export function ProviderDetailModal({ provider, onClose }: ProviderDetailModalProps) {
  const renderStars = (rating: number | null) => {
    if (rating === null) {
      return <span className="text-sm text-gray-400">No rating available</span>;
    }

    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center space-x-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-5 h-5 ${
              i < fullStars
                ? "text-yellow-400 fill-current"
                : i === fullStars && hasHalfStar
                ? "text-yellow-200 fill-current"
                : "text-gray-300"
            }`}
          />
        ))}
        <span className="ml-2 text-lg font-medium">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const getDataQualityColor = (score: number) => {
    if (score >= 90) return "text-green-600 bg-green-50";
    if (score >= 75) return "text-blue-600 bg-blue-50";
    if (score >= 60) return "text-yellow-600 bg-yellow-50";
    return "text-orange-600 bg-orange-50";
  };

  const getPerformanceColor = (days: number) => {
    if (days === 0) return "text-gray-400";
    if (days <= 10) return "text-green-600";
    if (days <= 20) return "text-blue-600";
    if (days <= 30) return "text-yellow-600";
    return "text-orange-600";
  };

  const getPerformanceLabel = (days: number) => {
    if (days === 0) return "No data";
    if (days <= 10) return "Excellent";
    if (days <= 20) return "Good";
    if (days <= 30) return "Fair";
    return "Needs Improvement";
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 relative">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
              <Building className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-white mb-2">{provider.name}</h2>
              <div className="flex items-center space-x-3">
                <span className="inline-block px-3 py-1 text-sm font-medium bg-white/20 backdrop-blur-sm text-white rounded-full">
                  {provider.organizationType}
                </span>
                <div className="flex items-center space-x-1 text-white/90">
                  <MapPin className="w-4 h-4" />
                  <span>{provider.city}, {provider.state} {provider.zipCode}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-8">
          {/* Performance Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="border-2 border-green-200 bg-green-50/50">
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <div className="text-3xl font-bold text-green-600">{provider.referralsAccepted}</div>
                <div className="text-sm text-gray-600 font-medium">Referrals Accepted</div>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 bg-blue-50/50">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-3xl font-bold text-blue-600">{provider.referralsMatched}</div>
                <div className="text-sm text-gray-600 font-medium">Referrals Matched</div>
              </CardContent>
            </Card>

            <Card className={`border-2 ${provider.avgMatchToCloseTime <= 10 ? 'border-green-200 bg-green-50/50' : provider.avgMatchToCloseTime <= 20 ? 'border-blue-200 bg-blue-50/50' : 'border-yellow-200 bg-yellow-50/50'}`}>
              <CardContent className="p-4 text-center">
                <Clock className={`w-8 h-8 ${getPerformanceColor(provider.avgMatchToCloseTime)} mx-auto mb-2`} />
                <div className={`text-3xl font-bold ${getPerformanceColor(provider.avgMatchToCloseTime)}`}>
                  {provider.avgMatchToCloseTime > 0 ? `${provider.avgMatchToCloseTime}d` : 'N/A'}
                </div>
                <div className="text-sm text-gray-600 font-medium">Avg Match Time</div>
                {provider.avgMatchToCloseTime > 0 && (
                  <div className={`text-xs mt-1 ${getPerformanceColor(provider.avgMatchToCloseTime)}`}>
                    {getPerformanceLabel(provider.avgMatchToCloseTime)}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className={`border-2 ${provider.dataQualityScore >= 90 ? 'border-green-200 bg-green-50/50' : provider.dataQualityScore >= 75 ? 'border-blue-200 bg-blue-50/50' : 'border-yellow-200 bg-yellow-50/50'}`}>
              <CardContent className="p-4 text-center">
                <Target className={`w-8 h-8 ${provider.dataQualityScore >= 90 ? 'text-green-600' : provider.dataQualityScore >= 75 ? 'text-blue-600' : 'text-yellow-600'} mx-auto mb-2`} />
                <div className={`text-3xl font-bold ${provider.dataQualityScore >= 90 ? 'text-green-600' : provider.dataQualityScore >= 75 ? 'text-blue-600' : 'text-yellow-600'}`}>
                  {provider.dataQualityScore}
                </div>
                <div className="text-sm text-gray-600 font-medium">Data Quality</div>
              </CardContent>
            </Card>
          </div>

          {/* Google Rating */}
          {provider.googleRating !== null && (
            <Card className="mb-6 border-2 border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-lg">
                  <Star className="w-6 h-6 text-yellow-500" />
                  <span>Google Reviews</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    {renderStars(provider.googleRating)}
                    <div className="text-sm text-gray-600 mt-1">
                      Based on {provider.googleReviewCount} reviews
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contact Information */}
          <Card className="mb-6 border-2 border-indigo-200 bg-indigo-50/30">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-lg">
                <Phone className="w-6 h-6 text-indigo-600" />
                <span>Contact Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {provider.streetAddress && (
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-800">{provider.streetAddress}</div>
                    <div className="text-gray-600">{provider.city}, {provider.state} {provider.zipCode}</div>
                  </div>
                </div>
              )}
              {provider.phoneNumber && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <a href={`tel:${provider.phoneNumber}`} className="text-blue-600 hover:text-blue-700 font-medium">
                    {provider.phoneNumber}
                  </a>
                </div>
              )}
              {provider.email && (
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <a href={`mailto:${provider.email}`} className="text-blue-600 hover:text-blue-700 font-medium">
                    {provider.email}
                  </a>
                </div>
              )}
              {provider.website && (
                <div className="flex items-center space-x-3">
                  <Globe className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-medium">
                    Visit Website
                  </a>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Services Offered */}
          <Card className="border-2 border-purple-200 bg-purple-50/30">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-lg">
                <CheckCircle className="w-6 h-6 text-purple-600" />
                <span>Services Offered</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {provider.services.map((service, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-2 text-sm font-medium bg-purple-100 text-purple-800 rounded-lg"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-8 py-4 bg-gray-50 flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-xl"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              const address = `${provider.streetAddress || ''}, ${provider.city}, ${provider.state} ${provider.zipCode}`;
              const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
              window.open(mapsUrl, '_blank');
            }}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 rounded-xl"
          >
            <MapPin className="w-4 h-4 mr-2" />
            Get Directions
          </Button>
        </div>
      </div>
    </div>
  );
}
